import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Input,
  CardHeader,
  CardFooter,
  Button,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import {
  getUser as onGetUser,
  updateUser as onUpdateUser,
  changeUserPassword as onChangeUserPassword,
} from "../../../slices/user/thunk";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { useParams, useNavigate } from "react-router-dom";

const selectUserState = createSelector(
  (state) => state.User, // Make sure this matches your store key
  (user) => ({
    user: user.user || null,
    isUserSuccess: user.isUserSuccess,
    isUserFetched: user.isUserFetched,
    isLoading: user.isLoading,
    error: user.error,
  })
);

const groupOptions = {
  Admin: "3",
  Editor: "2",
  Viewer: "1",
};

const UserDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isUserSuccess, isUserFetched, isLoading, error } = useSelector(selectUserState);
  const { userProfile, loading: profileLoading } = useProfile();
  const { id } = useParams();
  
  const isViewer = useMemo(() => {
    return userProfile?.user?.groups?.includes("Viewer") || false;
  }, [userProfile]);
  
  const [formData, setFormData] = useState(null);
  const [formDataPass, setFormDataPass] = useState({
    password: "",
    password_verify: "",
  });
  const [isEditing, setIsEditing] = useState(false);

  // Fetch user on component mount
  useEffect(() => {
    if (id) {
      dispatch(onGetUser(id));
    }
  }, [dispatch, id]);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData(user);
    }
  }, [user]);

  // Handle errors
  // useEffect(() => {
  //   if (error) {
  //     toast.error(error, { autoClose: 3000 });
      
  //     // If user not found, redirect back
  //     if (error.includes("not found") || error.includes("404")) {
  //       setTimeout(() => {
  //         navigate("/admin/panel/users");
  //       }, 2000);
  //     }
  //   }
  // }, [error, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    
    if (name === "group_id") {
      setFormData((prevData) => ({
        ...prevData,
        groups: Object.keys(groupOptions).find(
          (key) => groupOptions[key] === value
        ),
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setFormDataPass((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!formData?.id) {
      toast.error("User ID is required for update");
      return;
    }

    try {
      
      // Update user with new structure { id, ...userData }
      await dispatch(onUpdateUser({
        id: formData.id,
        ...formData
      })).unwrap();

      setIsEditing(false);
      // Don't show toast here as it's handled in the thunk
    } catch (error) {
      console.error("Save failed:", error);
      // Reset form data to original user data
      if (user) {
        setFormData(user);
      }
    }
  };

  const handleSavePass = async () => {
    // Client-side validation
    if (!formDataPass.password || !formDataPass.password_verify) {
      toast.error("Both password fields are required.", { autoClose: 3000 });
      return;
    }

    if (formDataPass.password !== formDataPass.password_verify) {
      toast.error("Passwords do not match.", { autoClose: 3000 });
      return;
    }

    if (formDataPass.password.length < 8) {
      toast.error("Password must be at least 8 characters long.", { autoClose: 3000 });
      return;
    }

    try {      
      await dispatch(
        onChangeUserPassword({
          user_id: formData?.id,
          password: formDataPass.password,
          password_verify: formDataPass.password_verify,
        })
      ).unwrap();

      // Clear password form on success
      setFormDataPass({ password: "", password_verify: "" });
      // Toast is handled in the thunk
    } catch (error) {
      console.error("Password reset failed:", error);
    }
  };

  const toggleEdit = () => {
    if (isEditing) {
      // Reset form data when canceling edit
      setFormData(user);
    }
    setIsEditing(!isEditing);
  };

  // Loading state
  if (isLoading && !user) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // User not found or error state
  if (!isLoading && !user && error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center flex-column"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <i className="ri-user-unfollow-line display-4 text-muted mb-3"></i>
              <h4>User Not Found</h4>
              <p className="text-muted">The user you're looking for doesn't exist or has been removed.</p>
              <Button 
                color="primary" 
                onClick={() => navigate("/admin/panel/users")}
              >
                Back to Users
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // User not loaded yet
  if (!formData) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading user details...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <CardHeader className="border-0 bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="card-title mb-0">User Details</h5>
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => navigate("/admin/panel/users")}
                  >
                    <i className="ri-arrow-left-line me-1"></i>
                    Back to Users
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="username">Username</Label>
                      <Input
                        type="text"
                        id="username"
                        name="username"
                        value={formData?.username || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="first_name">First Name</Label>
                      <Input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData?.first_name || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="last_name">Last Name</Label>
                      <Input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData?.last_name || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="email">Email</Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData?.email || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="group">Group</Label>
                      <Input
                        type="select"
                        id="group_id"
                        name="group_id"
                        value={groupOptions[formData?.groups] || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Group</option>
                        <option value="3">Admin</option>
                        <option value="2">Editor</option>
                        <option value="1">Viewer</option>
                      </Input>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3 form-check">
                      <Input
                        type="checkbox"
                        id="is_superuser"
                        name="is_superuser"
                        checked={!!formData?.is_superuser}
                        onChange={(e) =>
                          handleInputChange({
                            target: {
                              name: e.target.name,
                              value: e.target.checked,
                            },
                          })
                        }
                        disabled={!isEditing}
                        className="form-check-input"
                      />
                      <Label for="is_superuser" className="form-check-label">
                        Super User
                      </Label>
                    </div>
                    <div className="mb-3 form-check">
                      <Input
                        type="checkbox"
                        id="is_staff"
                        name="is_staff"
                        checked={!!formData?.is_staff}
                        onChange={(e) =>
                          handleInputChange({
                            target: {
                              name: e.target.name,
                              value: e.target.checked,
                            },
                          })
                        }
                        disabled={!isEditing}
                        className="form-check-input"
                      />
                      <Label for="is_staff" className="form-check-label">
                        Staff
                      </Label>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter className="d-flex justify-content-between align-items-center">
                {!isViewer && (
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={toggleEdit}
                      disabled={isLoading}
                    >
                      {isEditing ? "Cancel Edit" : "Edit"}
                    </Button>
                    {isEditing && (
                      <Button 
                        color="success" 
                        onClick={handleSave}
                        disabled={isLoading}
                      >
                        {isLoading ? "Saving..." : "Save"}
                      </Button>
                    )}
                  </div>
                )}
              </CardFooter>
            </Card>
          </Col>
        </Row>

        {/* Password Reset Section */}
        <Form>
          <Row className="justify-content-center">
            <Col md={8}>
              <Card>
                <CardHeader>
                  <Label>Password Reset</Label>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label for="password">New Password</Label>
                        <Input
                          type="password"
                          placeholder="Enter new password"
                          id="password"
                          name="password"
                          value={formDataPass.password}
                          onChange={handlePasswordChange}
                          autoComplete="new-password"
                          disabled={isLoading}
                        />
                        <div className="form-text">
                          Password must be at least 8 characters long
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label for="password_verify">Verify Password</Label>
                        <Input
                          type="password"
                          placeholder="Confirm new password"
                          id="password_verify"
                          name="password_verify"
                          value={formDataPass.password_verify}
                          onChange={handlePasswordChange}
                          autoComplete="new-password"
                          disabled={isLoading}
                        />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter className="d-flex justify-content-between align-items-center">
                  {!isViewer && (
                    <div className="d-flex gap-2">
                      <Button 
                        color="danger" 
                        onClick={handleSavePass}
                        disabled={isLoading || !formDataPass.password || !formDataPass.password_verify}
                      >
                        {isLoading ? "Resetting..." : "Reset Password"}
                      </Button>
                    </div>
                  )}
                </CardFooter>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default UserDetails;